import React, { Suspense, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, CalendarDays, Loader2 } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";

const EventRegistrations = React.lazy(() => import("./EventRegistrations"));
const Events = React.lazy(() => import("./Events"));

interface Event {
  id: string;
  name: string;
  event_date: string | null;
  registration_goal: number | null;
}

const TabLoadingFallback = () => (
  <div className="flex items-center justify-center p-12">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const EventsHub: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization, isLoading: orgLoading } = useOrganization();
  const activeTab = searchParams.get("tab") || "registrations";
  
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  useEffect(() => {
    if (!orgLoading && organization?.id) {
      loadEvents();
    }
  }, [organization?.id, orgLoading]);

  const loadEvents = async () => {
    if (!organization?.id) return;

    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('events')
      .select('id, name, event_date, registration_goal')
      .eq('organization_id', organization.id)
      .gte('event_date', today) // Only future/current events
      .order('event_date', { ascending: true }); // Nearest upcoming first

    if (!error && data) {
      setEvents(data);
      if (data.length > 0 && !selectedEventId) {
        setSelectedEventId(data[0].id);
      }
    }
  };

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);
  const eventGoal = selectedEvent?.registration_goal || 300;

  return (
    <div className="h-full">
      <div className="px-4 pt-4 md:px-8 md:pt-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold font-display">Events</h1>
        {events.length > 0 && (
          <Select value={selectedEventId || ""} onValueChange={setSelectedEventId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select event" />
            </SelectTrigger>
            <SelectContent>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>
                  {event.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="px-4 md:px-8 pt-4">
          <TabsList className="w-full md:w-auto grid grid-cols-2 md:inline-flex">
            <TabsTrigger value="registrations" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Registrations</span>
              <span className="sm:hidden">Regs</span>
            </TabsTrigger>
            <TabsTrigger value="manage" className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">Manage Events</span>
              <span className="sm:hidden">Events</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="registrations" className="mt-0 p-4 md:p-8">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <EventRegistrations 
                embedded 
                selectedEventId={selectedEventId}
                eventGoal={eventGoal}
              />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
        <TabsContent value="manage" className="mt-0 p-4 md:p-8">
          <ErrorBoundary>
            <Suspense fallback={<TabLoadingFallback />}>
              <Events embedded />
            </Suspense>
          </ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EventsHub;
